import React, { useState, useEffect } from 'react';
import { ShieldCheck, Mail, ArrowRight, RotateCw, LogOut } from 'lucide-react';
import {
  sendMfaEmailOtp,
  verifyMfaEmailOtp,
  setDeviceMfaTrusted,
  extractOtp,
  formatOtp,
} from '../lib/mfa';
import { useDialogA11y } from '../lib/useDialogA11y';

interface TwoFactorAuthModalProps {
  email: string;
  userId: string;
  onSuccess: () => void;
  onSignOut: () => Promise<void>;
}

export default function TwoFactorAuthModal({
  email,
  userId,
  onSuccess,
  onSignOut,
}: TwoFactorAuthModalProps) {
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);

  // Send koden automatisk ved åbning
  useEffect(() => {
    let mounted = true;
    void sendMfaEmailOtp(email).then((res) => {
      if (!mounted) return;
      if (res.success) setInfo('Engangskode er sendt til din e-mail.');
      else setError(res.message ?? 'Engangskoden kunne ikke sendes.');
    });
    return () => {
      mounted = false;
    };
  }, [email]);

  // Nedtælling til gensend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Escape må ikke springe MFA over: ingen onClose, men fokus-trap, fokus-retur og scroll-lock gælder.
  // Videre uden kode kræver et tydeligt klik på en af knapperne nedenfor.
  const { ref: dialogRef } = useDialogA11y();

  const submitCode = async (raw: string) => {
    if (busy || attemptsRemaining <= 0) return;
    const clean = raw.trim().replace(/\s+/g, '');
    if (clean.length < 6) {
      setError('Indtast venligst den fulde engangskode.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const ok = await verifyMfaEmailOtp(email, clean);
      if (ok) {
        if (rememberDevice) {
          setDeviceMfaTrusted(true, userId);
        }
        onSuccess();
      } else {
        const nextAttempts = attemptsRemaining - 1;
        setAttemptsRemaining(nextAttempts);
        if (nextAttempts <= 0) {
          setError('For mange forkerte forsøg. Klik på "Gensend kode" for at modtage en ny kode.');
        } else {
          setError(`Forkert kode. Du har ${nextAttempts} forsøg tilbage.`);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Koden kunne ikke verificeres.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitCode(code);
  };

  // Indsæt koden fra udklipsholderen med ét tryk (nemt på telefon).
  const handlePasteCode = async () => {
    setError(null);
    try {
      const otp = extractOtp(await navigator.clipboard.readText());
      if (otp) {
        setCode(formatOtp(otp));
        if (otp.length === 8) void submitCode(otp);
      } else {
        setError('Der ligger ingen kode i udklipsholderen. Kopiér koden fra mailen først.');
      }
    } catch {
      setError(
        'Din telefon tillod ikke at indsætte automatisk. Hold fingeren i feltet og vælg Indsæt.',
      );
    }
  };

  const handleResend = async () => {
    if (resending || countdown > 0) return;
    setResending(true);
    setError(null);
    try {
      const res = await sendMfaEmailOtp(email);
      if (!res.success) throw new Error(res.message ?? 'Koden kunne ikke gensendes.');
      setAttemptsRemaining(5);
      setCountdown(45);
      setInfo('En ny engangskode er sendt til din e-mail.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Koden kunne ikke gensendes.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      className="mfa-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfa-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 'calc(var(--z-popover) + 6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        className="mfa-modal-card"
        style={{
          backgroundColor: '#0d1811',
          border: '1px solid rgba(74, 222, 128, 0.35)',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '460px',
          margin: 'auto',
          padding: '32px 26px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.85)',
          color: '#e2e8f0',
          fontFamily: 'inherit',
          position: 'relative',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div
            style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              backgroundColor: 'rgba(74, 222, 128, 0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4ade80',
              marginBottom: '14px',
              border: '1px solid rgba(74, 222, 128, 0.3)',
            }}
          >
            <ShieldCheck size={28} />
          </div>
          <h2
            id="mfa-modal-title"
            style={{
              fontSize: '1.35rem',
              fontWeight: 800,
              color: '#ffffff',
              margin: '0 0 8px 0',
            }}
          >
            To-faktor bekræftelse
          </h2>
          <p style={{ fontSize: '0.88rem', color: '#94a3b8', margin: 0, lineHeight: 1.45 }}>
            Af hensyn til sikkerheden på rådgiver- og revirkonti har vi sendt en engangskode til:
            <br />
            <strong
              style={{
                color: '#ffffff',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                marginTop: 4,
              }}
            >
              <Mail size={14} color="#4ade80" /> {email}
            </strong>
          </p>
        </div>

        {info && (
          <div
            style={{
              backgroundColor: 'rgba(74, 222, 128, 0.12)',
              border: '1px solid rgba(74, 222, 128, 0.3)',
              color: '#bbf7d0',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '0.84rem',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            {info}
          </div>
        )}

        {error && (
          <div
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#fca5a5',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '0.84rem',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: '18px' }}>
            <label
              htmlFor="mfa-code-input"
              style={{
                display: 'block',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#cbd5e1',
                marginBottom: '8px',
                textAlign: 'center',
              }}
            >
              Indtast engangskode
            </label>
            <input
              id="mfa-code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9 ]*"
              autoComplete="one-time-code"
              maxLength={11}
              placeholder="0000 0000"
              value={code}
              disabled={busy || attemptsRemaining <= 0}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                if (val.length <= 8) setCode(formatOtp(val));
              }}
              onPaste={(e) => {
                const otp = extractOtp(e.clipboardData.getData('text'));
                if (otp) {
                  e.preventDefault();
                  setCode(formatOtp(otp));
                  if (otp.length === 8) void submitCode(otp);
                }
              }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                minHeight: '52px',
                textAlign: 'center',
                fontSize: '1.6rem',
                fontWeight: 700,
                letterSpacing: '0.25em',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(74, 222, 128, 0.35)',
                borderRadius: '10px',
                color: '#ffffff',
                outline: 'none',
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => void handlePasteCode()}
              disabled={busy || attemptsRemaining <= 0}
              style={{
                width: '100%',
                minHeight: '48px',
                marginTop: '10px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(74, 222, 128, 0.4)',
                color: '#4ade80',
                fontSize: '0.92rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Indsæt kode
            </button>
            <p
              style={{
                margin: '10px 0 0',
                fontSize: '0.78rem',
                color: '#94a3b8',
                textAlign: 'center',
                lineHeight: 1.45,
              }}
            >
              Tryk på koden i mailen og vælg Kopiér. Kom tilbage hertil og tryk &quot;Indsæt
              kode&quot; – så bekræftes den automatisk.
            </p>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: '0.84rem',
              color: '#cbd5e1',
              marginBottom: '22px',
              justifyContent: 'center',
            }}
          >
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: '#16a34a' }}
            />
            <span>Husk denne enhed i 30 dage</span>
          </label>

          <button
            type="submit"
            disabled={busy || attemptsRemaining <= 0}
            style={{
              width: '100%',
              minHeight: '48px',
              borderRadius: '10px',
              backgroundColor: '#15803d',
              border: '1px solid #22c55e',
              color: '#ffffff',
              fontSize: '0.94rem',
              fontWeight: 700,
              cursor: busy || attemptsRemaining <= 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(22, 163, 74, 0.4)',
              transition: 'all 0.15s ease',
              marginBottom: '14px',
            }}
          >
            {busy ? 'Bekræfter…' : 'Bekræft og gå til revir'}
            {!busy && <ArrowRight size={18} />}
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.82rem',
              color: '#94a3b8',
              paddingTop: '6px',
            }}
          >
            <button
              type="button"
              onClick={handleResend}
              disabled={countdown > 0 || resending}
              style={{
                background: 'none',
                border: 'none',
                color: countdown > 0 ? '#64748b' : '#4ade80',
                cursor: countdown > 0 ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 0',
                fontSize: 'inherit',
              }}
            >
              <RotateCw size={13} className={resending ? 'spin' : ''} />
              {countdown > 0 ? `Gensend kode om ${countdown}s` : 'Gensend kode'}
            </button>

            <button
              type="button"
              onClick={async () => {
                await onSignOut();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 0',
                fontSize: 'inherit',
              }}
            >
              <LogOut size={13} />
              Log ud
            </button>
          </div>

          <div
            style={{
              marginTop: '18px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>
              Modtager du ikke koden? Tjek spammappen, eller tryk på &quot;Gensend kode&quot; når
              nedtællingen er slut.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
